import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatRoundFixed, numberShorthand } from '@benzinga/utils/numberUtils';
import type { Fund } from '@benzinga/etf-manager';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { BaseQuoteProfile } from '../../../utils/quote/types';
import FAQs from '../../../components/FAQs';

interface ETFFAQProps {
  profile: BaseQuoteProfile;
  etfFund: Fund | null;
}

const ETFFAQ: React.FC<ETFFAQProps> = ({ profile, etfFund }) => {
  const { t } = useTranslation('quote');

  const quoteData = profile?.richQuoteData || null;

  if (!etfFund && !quoteData) {
    return null;
  }

  const FAQsDataSet = [
    {
      answer: getETFPerformance(quoteData, profile.symbol, t),
      question: t('Quote.FAQ.Questions.ETFs.how-has-etf-performed', { symbol: quoteData?.symbol }),
    },
    {
      answer: getExpenseRatio(etfFund, t),
      question: t('Quote.FAQ.Questions.ETFs.what-is-the-expense-ratio', { symbol: quoteData?.symbol }),
    },
    {
      answer: getAUM(etfFund, t),
      question: t('Quote.FAQ.Questions.ETFs.what-is-aum', { symbol: quoteData?.symbol }),
    },
    {
      answer: getInvestmentStyle(etfFund, t),
      question: t('Quote.FAQ.Questions.ETFs.what-is-investment-style', { symbol: quoteData?.symbol }),
    },
    {
      answer: getTopHoldings(etfFund, t),
      question: t('Quote.FAQ.Questions.ETFs.what-are-top-holdings', { symbol: quoteData?.symbol }),
    },
    {
      answer: getAverageDailyVolume(etfFund, t),
      question: t('Quote.FAQ.Questions.ETFs.what-is-the-avg-daily-volume', { symbol: quoteData?.symbol }),
    },
  ];

  const title = quoteData ? `${quoteData.name} (${quoteData.symbol})` : '';

  return <FAQs FAQsDataSet={FAQsDataSet} title={title} />;
};

export default ETFFAQ;

const getETFPerformance = (quoteData: DelayedQuote | null, symbol: string, t: TFunction): React.ReactElement => {
  if (!quoteData) {
    return <p>{t('Quote.FAQ.Answers.ETFs.no-performance-data-available', { symbol })}</p>;
  }

  const changePercent = quoteData.changePercent || 0;
  const fiftyTwoWeekHigh = quoteData.fiftyTwoWeekHigh || 0;
  const fiftyTwoWeekLow = quoteData.fiftyTwoWeekLow || 0;
  const currentPrice = quoteData.lastTradePrice || 0;

  return (
    <p>
      {t('Quote.FAQ.Answers.ETFs.etf-performance', {
        changePercent: formatRoundFixed(changePercent, 2),
        currentPrice: formatRoundFixed(currentPrice, 2),
        fiftyTwoWeekHigh: formatRoundFixed(fiftyTwoWeekHigh, 2),
        fiftyTwoWeekLow: formatRoundFixed(fiftyTwoWeekLow, 2),
        symbol: quoteData.symbol || 'N/A',
      })}
    </p>
  );
};

const getExpenseRatio = (etfData: Fund | null, t: TFunction): React.ReactElement => {
  if (!etfData || !etfData.avg_expense_ratio) {
    return <p>{t('Quote.FAQ.Answers.ETFs.no-expense-ratio-data', { symbol: etfData?.fund_symbol })}</p>;
  }
  return (
    <p>
      {t('Quote.FAQ.Answers.ETFs.expense-ratio', {
        expenseRatio: etfData.avg_expense_ratio,
        symbol: etfData?.fund_symbol,
      })}
    </p>
  );
};

const getAUM = (etfData: Fund | null, t: TFunction): React.ReactElement => {
  if (!etfData || !etfData.fund_aum) {
    return <p>{t('Quote.FAQ.Answers.ETFs.no-aum-data', { symbol: etfData?.fund_symbol })}</p>;
  }
  return (
    <p>
      {t('Quote.FAQ.Answers.ETFs.aum', {
        aum: etfData?.fund_aum ? numberShorthand(etfData?.fund_aum, 2) : '-',
        symbol: etfData?.fund_symbol,
      })}
    </p>
  );
};

const getInvestmentStyle = (etfData: Fund | null, t: TFunction): React.ReactElement => {
  if (!etfData || !etfData.investment_style) {
    return <p>{t('Quote.FAQ.Answers.ETFs.no-investment-style-data', { symbol: etfData?.fund_symbol })}</p>;
  }
  return (
    <p>
      {t('Quote.FAQ.Answers.ETFs.investment-style', {
        style: etfData.investment_style,
        symbol: etfData?.fund_symbol,
      })}
    </p>
  );
};

const getTopHoldings = (etfData: Fund | null, t: TFunction): React.ReactElement => {
  if (!etfData || !Array.isArray(etfData.fund_top_holders) || etfData.fund_top_holders.length === 0) {
    return <p>{t('Quote.FAQ.Answers.ETFs.no-top-holdings-data', { symbol: etfData?.fund_symbol })}</p>;
  }
  const topHoldings = etfData.fund_top_holders
    .slice(0, 5)
    .map(holding => `${holding.symbol} (${formatRoundFixed(holding.sharepercentage, 2)}%)`)
    .join(', ');

  return (
    <p>
      {t('Quote.FAQ.Answers.ETFs.top-holdings', {
        holdings: topHoldings,
        symbol: etfData?.fund_symbol,
      })}
    </p>
  );
};

const getAverageDailyVolume = (etfData: Fund | null, t: TFunction): React.ReactElement => {
  if (!etfData || !etfData.avg_daily_volume) {
    return <p>{t('Quote.FAQ.Answers.ETFs.no-avg-daily-volume-data', { symbol: etfData?.fund_symbol })}</p>;
  }
  return (
    <p>
      {t('Quote.FAQ.Answers.ETFs.avg-daily-volume', {
        symbol: etfData?.fund_symbol,
        volume: etfData?.avg_vol_30days ? numberShorthand(etfData.avg_vol_30days, 2) : '-',
      })}
    </p>
  );
};
